import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { AlertCircle, Bell, CheckCircle2, HelpCircle, History, KeyRound, Pause, Play, Plus, QrCode, RefreshCw, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { FalhaAoCarregar } from "@/components/admin/FalhaAoCarregar";
import { formatWhatsApp } from "@/lib/utils";

// Provedores em Rodízio (Pedro, 26/10/2026): um bloco por número de WhatsApp no /results. Cada número tem a UAZAPI e,
// opcional, a Z-API de redundância, como o cartão único de antes. Toda ação devolve o GET inteiro, que vai direto para o
// cache.

type Lado = "uazapi" | "zapi";
type Estado = "conectado" | "caiu" | "pausado" | "aguardando_leitura" | "desconectado";

interface Credenciais {
  uazapiToken: string | null;
  zapiClientToken: string | null;
  zapiInstanceId: string | null;
  zapiInstanceToken: string | null;
}

interface Provedor {
  id: string;
  nome: string;
  ordem: number;
  padrao: boolean;
  finalNumero: string | null;
  telefone: string | null;
  estado: Estado;
  desde: string | null;
  ativo: Lado;
  credenciais: Credenciais;
  zapi: { conectada: boolean; fila: number | null; vinhaDe: string | null } | null;
  leads: number;
  contaOficial: boolean;
  qrcode: string | null;
}

interface EventoDoProvedor {
  id: string;
  occurredAt: string;
  eventType: string;
  provider: Lado | null;
  status: string | null;
  details: Record<string, unknown> | null;
  triggeredByUserId: string | null;
  numeroId?: string | null;
  numeroNome?: string | null;
}

interface ProvedoresResponse {
  responsavelId: string | null;
  // A pausa geral (envios_pausados, por SQL): guarda os envios de todos os números.
  pausaGeral?: boolean;
  wablastApiKeyFinal: string | null;
  nenhumConectadoDesde: string | null;
  enviosGuardados: number | null;
  provedores: Provedor[];
  history: EventoDoProvedor[];
}

const PROVEDORES_KEY = ["/api/admin/provedores"];
const CAMPOS = ["uazapiToken", "zapiClientToken", "zapiInstanceId", "zapiInstanceToken"] as const;
const ROTULOS: Record<(typeof CAMPOS)[number], string> = {
  uazapiToken: "UAZAPI · Token",
  zapiClientToken: "Z-API · Client Token",
  zapiInstanceId: "Z-API · ID da instância",
  zapiInstanceToken: "Z-API · Token da instância",
};
const EXEMPLOS: Record<(typeof CAMPOS)[number], string> = {
  uazapiToken: "UUID UAZAPI",
  zapiClientToken: "Fa7dbd0...",
  zapiInstanceId: "3F434B...",
  zapiInstanceToken: "FA2EA8...",
};
const PILULA = "px-2 py-0.5 rounded-full text-[11px] font-medium border";
const CAMPO = "mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono";

function horaOuData(iso: string): string {
  const d = new Date(iso);
  const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === new Date().toDateString()) return hora;
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}, ${hora}`;
}

function pilulaDoEstado(p: Provedor): { label: string; color: string } {
  switch (p.estado) {
    case "conectado":
      return { label: "Conectado", color: "bg-green-50 text-green-700 border-green-300" };
    case "caiu": {
      const quando = p.desde ? horaOuData(p.desde) : null;
      return { label: quando ? `Caiu ${quando.includes("/") ? "em" : "às"} ${quando}` : "Caiu", color: "bg-red-50 text-red-700 border-red-300" };
    }
    case "pausado":
      return { label: "Pausado", color: "bg-gray-50 text-gray-600 border-gray-300" };
    case "aguardando_leitura":
      return { label: "Aguardando leitura", color: "bg-amber-50 text-amber-700 border-amber-300" };
    default:
      return { label: "Desconectado", color: "bg-gray-50 text-gray-600 border-gray-300" };
  }
}

function rotuloDoEvento(e: EventoDoProvedor): { color: string; label: string; icon: typeof CheckCircle2 } {
  switch (e.eventType) {
    case "caiu":
    case "provider_unavailable":
      return { color: "text-red-700 bg-red-50 border-red-300", label: e.eventType === "caiu" ? "Caiu" : "Ambos fora", icon: AlertCircle };
    case "voltou":
    case "retomado":
    case "adicionado":
    case "conectado":
      return { color: "text-green-700 bg-green-50 border-green-300", label: { voltou: "Voltou", retomado: "Retomado", adicionado: "Adicionado", conectado: "Conectado" }[e.eventType], icon: CheckCircle2 };
    case "pausado":
      return { color: "text-gray-600 bg-gray-50 border-gray-300", label: "Pausado", icon: Pause };
    case "removido":
    case "fila_apagada":
      return { color: "text-gray-600 bg-gray-50 border-gray-300", label: e.eventType === "removido" ? "Removido" : "Fila apagada", icon: Trash2 };
    case "responsavel_trocado":
    case "provider_switched":
      return { color: "text-amber-700 bg-amber-50 border-amber-300", label: e.eventType === "provider_switched" ? "Switch" : "Responsável", icon: RefreshCw };
    case "alerta_enviado":
      return { color: "text-blue-700 bg-blue-50 border-blue-300", label: "Alerta", icon: Bell };
    case "zapi_movida":
      return { color: "text-purple-700 bg-purple-50 border-purple-300", label: "Z-API", icon: RefreshCw };
    case "credentials_updated":
      return { color: "text-blue-700 bg-blue-50 border-blue-300", label: "Credencial atualizada", icon: KeyRound };
    default:
      return { color: "text-gray-600 bg-gray-50 border-gray-300", label: e.eventType, icon: HelpCircle };
  }
}

function textoDoEvento(e: EventoDoProvedor, nomeDe: (id: unknown) => string): string {
  const at = new Date(e.occurredAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const d = e.details ?? {};
  const numero = e.numeroNome ?? (e.numeroId ? nomeDe(e.numeroId) : null);
  const de = (d["deNome"] as string | undefined) ?? nomeDe(d["de"]);
  const para = (d["paraNome"] as string | undefined) ?? nomeDe(d["para"]);
  switch (e.eventType) {
    case "caiu":
    case "voltou":
    case "pausado":
    case "retomado":
    case "adicionado":
    case "removido":
    case "conectado":
      return `${at} · ${numero ?? "?"} ${{ caiu: "caiu", voltou: "voltou", pausado: "pausado", retomado: "retomado", adicionado: "adicionado", removido: "removido", conectado: "conectado" }[e.eventType]}`;
    case "responsavel_trocado":
      return `${at} · /check e /details: ${de} → ${para}${d["automatico"] ? " (automático, na queda)" : ""}`;
    case "fila_apagada":
      return `${at} · Z-API do ${numero ?? "?"}${d["ok"] === false ? " (não conseguiu apagar)" : ""}`;
    case "alerta_enviado":
      return `${at} · ${numero ? `${numero}: ` : ""}enviado pelo ${(d["porNumeroNome"] as string | undefined) ?? nomeDe(d["porNumeroId"])}`;
    case "zapi_movida":
      return `${at} · Z-API do ${de} agora é a redundância do ${para}`;
    case "provider_switched":
      return `${at} · ${numero ? `${numero}: ` : ""}Switch ${d["from"] ?? "?"} → ${d["to"] ?? "?"} (motivo: ${d["failureReason"] ?? "?"})`;
    case "provider_unavailable": {
      const uErr = (d["uazapiError"] as { reason?: string } | null)?.reason ?? "?";
      const zErr = (d["zapiError"] as { reason?: string } | null)?.reason ?? "?";
      return `${at} · ${numero ? `${numero}: ` : ""}Ambos fora (ativo: ${d["activeAtTime"] ?? "?"}; uazapi: ${uErr}; z-api: ${zErr})`;
    }
    case "credentials_updated":
      return `${at} · ${numero ? `${numero}: ` : ""}Credenciais atualizadas: ${(d["updatedFields"] as string[] | undefined)?.join(", ") ?? "?"}`;
    default:
      return `${at} · ${e.eventType}${numero ? ` (${numero})` : ""}`;
  }
}

type Agir = (ocupacao: string, url: string, init: RequestInit, falha: string, ok?: string) => Promise<boolean>;
const corpo = (method: string, body: unknown = {}): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export function ProvedoresWhatsappPanel() {
  const { data, error, isLoading, isFetching, refetch } = useQuery<ProvedoresResponse>({
    queryKey: PROVEDORES_KEY,
    queryFn: () => customFetch("/api/admin/provedores", { method: "GET" }),
    // Esperando a leitura, o QR troca a cada ~20 s.
    refetchInterval: (query) => (query.state.data?.provedores.some((p) => p.estado === "aguardando_leitura") ? 4_000 : 60_000),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [mostrarEventos, setMostrarEventos] = useState(false);

  const agir: Agir = async (ocupacao, url, init, falha, ok) => {
    setOcupado(ocupacao);
    try {
      const res = await customFetch<ProvedoresResponse>(url, init);
      queryClient.setQueryData(PROVEDORES_KEY, res);
      if (ok) toast({ title: ok });
      return true;
    } catch (err: any) {
      toast({ title: "Erro", description: extractApiErrorMessage(err, falha), variant: "destructive" });
      return false;
    } finally {
      setOcupado(null);
    }
  };

  const provedores = [...(data?.provedores ?? [])].sort((a, b) => a.ordem - b.ordem);
  const history = data?.history ?? [];
  const nomeDe = (id: unknown) => provedores.find((p) => p.id === id)?.nome ?? "número removido";
  const responsavel = provedores.find((p) => p.id === data?.responsavelId) ?? null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Provedor WhatsApp
          </CardTitle>
          {data && !data.nenhumConectadoDesde && provedores.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Responsável pelo /check e /details:</span>
              <Select
                value={data.responsavelId ?? ""}
                onValueChange={(v) => {
                  if (v === data.responsavelId) return;
                  void agir("responsavel", "/api/admin/provedores/responsavel", corpo("PUT", { provedorId: v }), "Não foi possível trocar o responsável.", `Responsável: ${nomeDe(v)}`);
                }}
                disabled={ocupado !== null}
              >
                <SelectTrigger className="h-8 w-[140px] text-sm" data-testid="provedor-responsavel">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {provedores
                    .filter((p) => p.estado === "conectado" || p.id === responsavel?.id)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id} disabled={p.estado !== "conectado"}>
                        {p.nome}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Carregando…</div>
        ) : !data ? (
          <FalhaAoCarregar tela="results-provedores" erro={error} onTentar={() => refetch()} tentando={isFetching} />
        ) : (
          <>
            {data.nenhumConectadoDesde && (
              <div className="space-y-2" data-testid="provedores-todos-caidos">
                <div className="flex items-center gap-2 p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Nenhum número conectado desde {horaOuData(data.nenhumConectadoDesde)}.
                </div>
                {!!data.enviosGuardados && (
                  <p className="text-xs text-muted-foreground" data-testid="provedores-guardados">
                    {data.enviosGuardados} {data.enviosGuardados === 1 ? "envio guardado" : "envios guardados"} · saem aos poucos quando um número voltar
                  </p>
                )}
              </div>
            )}

            {data.pausaGeral && (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800" data-testid="provedores-pausa-geral">
                <Pause className="w-4 h-4 shrink-0" />
                Pausa geral ligada: os envios de todos os números ficam guardados{data.enviosGuardados ? ` (${data.enviosGuardados} até agora)` : ""}.
              </div>
            )}

            {provedores.map((p) => (
              <BlocoDoNumero
                key={p.id}
                p={p}
                provedores={provedores}
                removivel={!p.padrao && p.id !== data.responsavelId && provedores.length > 1}
                ocupado={ocupado}
                agir={agir}
                nomeDe={nomeDe}
              />
            ))}

            {adicionando ? (
              <NumeroNovo ocupado={ocupado} agir={agir} onFechar={() => setAdicionando(false)} />
            ) : (
              <button
                type="button"
                onClick={() => setAdicionando(true)}
                className="w-full h-10 rounded-lg border border-dashed text-sm font-medium text-primary hover:bg-primary/5 cursor-pointer flex items-center justify-center gap-1"
                data-testid="provedor-adicionar"
              >
                <Plus className="w-4 h-4" /> Adicionar Provedor
              </button>
            )}

            <ChaveWablast final={data.wablastApiKeyFinal} onSalva={() => refetch()} />

            <div className="border-t pt-3">
              <button
                type="button"
                onClick={() => setMostrarEventos(!mostrarEventos)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
                data-testid="provedores-eventos"
              >
                <History className="w-3 h-3" />
                Eventos recentes ({history.length}) {mostrarEventos ? "▲" : "▼"}
              </button>
              {mostrarEventos && (
                <div className="mt-2 space-y-1 max-h-64 overflow-y-auto text-xs">
                  {history.length === 0 && <div className="text-muted-foreground">Sem registros ainda.</div>}
                  {history.map((e) => {
                    const badge = rotuloDoEvento(e);
                    const Icon = badge.icon;
                    return (
                      <div key={e.id} className="flex items-start gap-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${badge.color} shrink-0`}>
                          <Icon className="w-3 h-3" />
                          {badge.label}
                        </span>
                        <span className="text-muted-foreground font-mono">{textoDoEvento(e, nomeDe)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BlocoDoNumero({
  p,
  provedores,
  removivel,
  ocupado,
  agir,
  nomeDe,
}: {
  p: Provedor;
  provedores: Provedor[];
  removivel: boolean;
  ocupado: string | null;
  agir: Agir;
  nomeDe: (id: unknown) => string;
}) {
  const confirm = useConfirm();
  const [rascunho, setRascunho] = useState<(Record<(typeof CAMPOS)[number], string> & { ativo: Lado }) | null>(null);
  const base = { ...Object.fromEntries(CAMPOS.map((k) => [k, p.credenciais[k] ?? ""])), ativo: p.ativo } as Record<(typeof CAMPOS)[number], string> & { ativo: Lado };
  const r = rascunho ?? base;
  const mudou = rascunho !== null && (CAMPOS.some((k) => (r[k].trim() || null) !== (p.credenciais[k] || null)) || r.ativo !== p.ativo);
  const travado = ocupado !== null;
  const estado = pilulaDoEstado(p);
  const zapiLevada = provedores.find((q) => q.zapi?.vinhaDe === p.id);
  const temZapi = p.zapi !== null;

  async function remover() {
    if (!(await confirm({
      title: `Remover o ${p.nome}?`,
      description: `O número sai da lista do Venda no Zap. A instância continua na UAZAPI.${p.leads > 0 ? ` Os ${p.leads} contatos dele passam para o ${provedores.find((q) => q.padrao)?.nome ?? "número padrão"}.` : ""}`,
      confirmLabel: "Remover",
    }))) return;
    await agir(`remover-${p.id}`, `/api/admin/provedores/${p.id}`, { method: "DELETE" }, "Não foi possível remover o número.", `${p.nome} removido`);
  }

  async function pausar() {
    if (!(await confirm({
      title: `Pausar o ${p.nome}?`,
      description: "Fica fora do rodízio, do /check e dos envios até você retomar.",
      confirmLabel: "Pausar",
    }))) return;
    await agir(`pausar-${p.id}`, `/api/admin/provedores/${p.id}/pausar`, corpo("POST"), "Não foi possível pausar o número.");
  }

  async function salvar() {
    const body: Record<string, string | null> = { ativo: r.ativo };
    for (const k of CAMPOS) body[k] = r[k].trim() || null;
    const ok = await agir(`salvar-${p.id}`, `/api/admin/provedores/${p.id}`, corpo("PUT", body), "Falha ao salvar o número.", `${p.nome} salvo`);
    if (ok) setRascunho(null);
  }

  const conectar = () => agir(`conectar-${p.id}`, `/api/admin/provedores/${p.id}/conectar`, corpo("POST"), "Não foi possível gerar o QR code.");

  const cabecalho = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-sm">{p.nome}</span>
      {p.telefone ? (
        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded border bg-muted/40 text-muted-foreground" data-testid={`provedor-telefone-${p.ordem}`}>{formatWhatsApp(p.telefone)}</span>
      ) : p.finalNumero ? (
        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded border bg-muted/40 text-muted-foreground">final {p.finalNumero}</span>
      ) : null}
      <span className={`${PILULA} ${estado.color}`} data-testid={`provedor-estado-${p.ordem}`}>{estado.label}</span>
      {p.estado === "conectado" && (
        <>
          <span className="text-xs text-muted-foreground">Ativo:</span>
          <span className={`${PILULA} ${p.ativo === "uazapi" ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-purple-50 text-purple-700 border-purple-300"}`}>
            {p.ativo === "uazapi" ? "UAZAPI" : "Z-API"}
          </span>
        </>
      )}
      {p.padrao && <span className="text-[11px] px-1.5 py-0.5 rounded border text-muted-foreground">padrão</span>}
    </div>
  );

  if (p.estado === "caiu") {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50/40 p-3 space-y-2" data-testid={`provedor-bloco-${p.ordem}`}>
        {cabecalho}
        {zapiLevada && <p className="text-xs text-red-700">A Z-API dele passou para o {zapiLevada.nome}.</p>}
        <div className="flex justify-end gap-2">
          {removivel && (
            <Button variant="outline" size="sm" className="text-destructive" onClick={remover} disabled={travado} data-testid={`provedor-remover-${p.ordem}`}>
              Remover
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={conectar} disabled={travado} data-testid={`provedor-conectar-${p.ordem}`}>
            <QrCode className="w-4 h-4 mr-1" /> Conectar
          </Button>
        </div>
      </div>
    );
  }

  if (p.estado === "pausado") {
    return (
      <div className="rounded-lg border bg-muted/40 p-3 space-y-2" data-testid={`provedor-bloco-${p.ordem}`}>
        {cabecalho}
        <p className="text-xs text-muted-foreground">Fora do rodízio, do /check e dos envios.</p>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => agir(`retomar-${p.id}`, `/api/admin/provedores/${p.id}/retomar`, corpo("POST"), "Não foi possível retomar o número.")}
            disabled={travado}
            data-testid={`provedor-retomar-${p.ordem}`}
          >
            <Play className="w-4 h-4 mr-1" /> Retomar
          </Button>
        </div>
      </div>
    );
  }

  const info = [
    p.contaOficial ? "código pela API Oficial" : "código pela UAZAPI",
    p.zapi
      ? p.zapi.conectada
        ? `Z-API conectada neste número${p.zapi.vinhaDe ? ` (era do ${nomeDe(p.zapi.vinhaDe)})` : ""} · fila ${p.zapi.fila ?? "?"}`
        : "Z-API desconectada"
      : "sem Z-API",
    ...(p.leads > 0 ? [`${p.leads} leads`] : []),
  ].join(" · ");

  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid={`provedor-bloco-${p.ordem}`}>
      {cabecalho}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CAMPOS.map((k) => (
          <div key={k}>
            <Label className="text-xs">
              {ROTULOS[k]}
              {k !== "uazapiToken" && !temZapi && <span className="text-muted-foreground font-normal"> (opcional)</span>}
            </Label>
            <input
              type="text"
              value={r[k]}
              onChange={(e) => setRascunho({ ...r, [k]: e.target.value })}
              placeholder={EXEMPLOS[k]}
              className={CAMPO}
              autoComplete="off"
              data-testid={`provedor-${p.ordem}-${k}`}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`provedor-info-${p.ordem}`}>{info}</p>
      {p.estado === "aguardando_leitura" && (
        <div className="flex flex-col items-center gap-2 pt-1">
          {p.qrcode ? (
            <img src={p.qrcode} alt={`QR code do ${p.nome}`} className="w-56 h-56 rounded-md border bg-white p-2" data-testid={`provedor-qr-${p.ordem}`} />
          ) : (
            <div className="text-sm text-muted-foreground">Gerando QR code…</div>
          )}
          <p className="text-xs text-muted-foreground">Leia com o WhatsApp do número novo: Aparelhos conectados › Conectar aparelho.</p>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t">
        <div className="flex items-center gap-2">
          {temZapi && (
            <>
              <Label className="text-xs">Forçar ativo:</Label>
              <Select value={r.ativo} onValueChange={(v) => setRascunho({ ...r, ativo: v as Lado })}>
                <SelectTrigger className="h-8 w-[120px] text-sm" data-testid={`provedor-ativo-${p.ordem}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uazapi">UAZAPI</SelectItem>
                  <SelectItem value="zapi">Z-API</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {removivel && p.estado !== "conectado" && (
            <Button variant="outline" size="sm" className="text-destructive" onClick={remover} disabled={travado} data-testid={`provedor-remover-${p.ordem}`}>
              Remover
            </Button>
          )}
          {p.estado === "desconectado" && (
            <Button variant="outline" size="sm" onClick={conectar} disabled={travado || mudou} data-testid={`provedor-conectar-${p.ordem}`}>
              <QrCode className="w-4 h-4 mr-1" /> Conectar
            </Button>
          )}
          {p.estado === "conectado" && (
            <Button variant="outline" size="sm" onClick={pausar} disabled={travado} data-testid={`provedor-pausar-${p.ordem}`}>
              <Pause className="w-4 h-4 mr-1" /> Pausar
            </Button>
          )}
          {mudou && (
            <Button variant="outline" size="sm" onClick={() => setRascunho(null)} disabled={travado}>
              Descartar
            </Button>
          )}
          <Button size="sm" onClick={salvar} disabled={!mudou || travado} data-testid={`provedor-salvar-${p.ordem}`}>
            {ocupado === `salvar-${p.id}` ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NumeroNovo({ ocupado, agir, onFechar }: { ocupado: string | null; agir: Agir; onFechar: () => void }) {
  const [campos, setCampos] = useState<Record<(typeof CAMPOS)[number], string>>({ uazapiToken: "", zapiClientToken: "", zapiInstanceId: "", zapiInstanceToken: "" });

  async function salvar() {
    const body: Record<string, string> = { uazapiToken: campos.uazapiToken.trim() };
    for (const k of CAMPOS) if (k !== "uazapiToken" && campos[k].trim()) body[k] = campos[k].trim();
    if (await agir("novo", "/api/admin/provedores", corpo("POST", body), "Não foi possível adicionar o número.")) onFechar();
  }

  return (
    <div className="rounded-lg border border-primary/40 p-3 space-y-3" data-testid="provedor-novo">
      <div className="font-semibold text-sm">Número novo</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CAMPOS.map((k) => (
          <div key={k}>
            <Label className="text-xs">
              {ROTULOS[k]}
              {k !== "uazapiToken" && <span className="text-muted-foreground font-normal"> (opcional)</span>}
            </Label>
            <input
              type="text"
              value={campos[k]}
              onChange={(e) => setCampos({ ...campos, [k]: e.target.value })}
              placeholder={EXEMPLOS[k]}
              className={CAMPO}
              autoComplete="off"
              data-testid={`provedor-novo-${k}`}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Ao salvar, aparece o QR code. Depois de lido, o número entra no rodízio e a Atendente passa a responder nele.</p>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onFechar} disabled={ocupado !== null}>
          Cancelar
        </Button>
        <Button size="sm" onClick={salvar} disabled={!campos.uazapiToken.trim() || ocupado !== null} data-testid="provedor-novo-salvar">
          {ocupado === "novo" ? "Salvando…" : "Salvar"}
        </Button>
      </div>
    </div>
  );
}

function ChaveWablast({ final, onSalva }: { final: string | null; onSalva: () => void }) {
  const { toast } = useToast();
  // A chave nunca volta inteira do servidor: undefined = manter, texto = trocar, null = apagar.
  const [chave, setChave] = useState<string | null | undefined>(undefined);
  const [salvando, setSalvando] = useState(false);
  const mudou = chave === null ? !!final : !!chave?.trim();

  async function salvar() {
    setSalvando(true);
    try {
      await customFetch("/api/admin/uazapi/credentials", corpo("PUT", { wablastApiKey: chave?.trim() || null }));
      toast({ title: chave === null ? "Chave apagada" : "Chave salva" });
      setChave(undefined);
      onSalva();
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: extractApiErrorMessage(err, "Falha ao salvar a chave."), variant: "destructive" });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">WaBlast · Chave da API Oficial</Label>
        {chave === null ? (
          <button type="button" className="text-xs text-primary hover:underline cursor-pointer" onClick={() => setChave(undefined)}>
            Desfazer
          </button>
        ) : final ? (
          <button type="button" className="text-xs text-destructive hover:underline cursor-pointer" onClick={() => setChave(null)}>
            Remover
          </button>
        ) : null}
      </div>
      <div className="mt-1 flex gap-2">
        <input
          type="text"
          value={chave ?? ""}
          onChange={(e) => setChave(e.target.value)}
          disabled={chave === null}
          placeholder={final ? `••••••••${final}` : "Cole a chave da WaBlast"}
          className="flex-1 min-w-0 h-9 px-3 rounded-md border border-input bg-background text-sm font-mono disabled:opacity-50"
          autoComplete="off"
          data-testid="wablast-chave"
        />
        {mudou && (
          <Button size="sm" className="h-9" onClick={salvar} disabled={salvando} data-testid="wablast-chave-salvar">
            {salvando ? "Salvando…" : "Salvar"}
          </Button>
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground" data-testid="wablast-chave-nota">
        {chave === null
          ? "Apagada ao salvar: os códigos voltam a sair pela UAZAPI."
          : final
            ? "Vale para todos os números. Para trocar, cole a chave nova."
            : "Sem chave, os códigos saem pela UAZAPI."}
      </p>
    </div>
  );
}
